import { useParams, Link, useNavigate } from 'react-router-dom'
import { useCollaborations } from '@/hooks/useCollaborations'
import { useState, useMemo } from 'react'
import { Calendar, CalendarX, Clock, Bell, BellOff, CalendarPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { toast } from '@/components/ui/sonner'
import { format } from 'date-fns'
import { formatDateOnly, getDateOnlyDiffInDays, parseDateOnly } from '@/lib/dateOnly'

const ScheduledPost = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { collaborations } = useCollaborations()
  const campaign = useMemo(() => collaborations.find((c) => c.id === id), [id, collaborations])

  const [remindersEnabled, setRemindersEnabled] = useState(true)
  const [scheduleAdded, setScheduleAdded] = useState(false)

  if (!campaign) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold mb-4">Campaign Not Found</h2>
        <p className="mb-4">The campaign you're looking for doesn't exist.</p>
        <Link to="/" className="text-blue-500 hover:underline">
          Return to Dashboard
        </Link>
      </div>
    )
  }

  // Calculate a target post date 3 days from campaign start
  const postDate = parseDateOnly(campaign.contentDueDate)
  const formattedDate =
    formatDateOnly(campaign.contentDueDate, 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }) ?? 'TBD'
  const formattedTime = postDate ? format(postDate, 'h:mm a') : 'TBD'

  const handleAddToCalendar = () => {
    // In a real app, this would generate an ICS file or open a calendar integration
    setScheduleAdded(true)
    toast('Added to Calendar', {
      description: `Posting reminder for ${formattedDate} has been added to your calendar.`,
    })
  }

  const handleToggleReminders = () => {
    setRemindersEnabled(!remindersEnabled)

    toast(remindersEnabled ? 'Reminders disabled' : 'Reminders enabled', {
      description: remindersEnabled
        ? 'You will no longer receive notifications for this post.'
        : 'You will receive notifications for this scheduled post.',
    })
  }

  // Calculate days remaining until scheduled post
  const daysRemaining = getDateOnlyDiffInDays(campaign.contentDueDate)

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-foreground">Scheduled Post</h1>
          {daysRemaining !== null && daysRemaining <= 2 ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-900 text-orange-200">
              Posting Deadline Approaching
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900 text-blue-200">
              Scheduled
            </span>
          )}
        </div>
        <p className="text-gray-500">
          {campaign.title} • {campaign.brand.name}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-foreground shadow-sm rounded-xl overflow-hidden mb-6">
            <div className="p-6">
              <h2 className="text-lg text-black font-semibold mb-4">Posting Schedule</h2>

              <div className="bg-blue-950 p-6 rounded-lg mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center text-blue-500">
                      <Calendar className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-300">Scheduled for</p>
                      <p className="text-xl font-medium text-blue-100">{formattedDate}</p>
                      <p className="text-blue-300">
                        {formattedTime} •{' '}
                        {Array.isArray(campaign.platform)
                          ? campaign.platform.join(', ')
                          : campaign.platform}
                      </p>
                    </div>
                  </div>

                  {/* <Button
                    variant="outline"
                    className="bg-foreground border-blue-800 text-blue-300 hover:bg-blue-950"
                    onClick={handleAddToCalendar}
                    disabled={scheduleAdded}
                  >
                    {scheduleAdded ? (
                      <>
                        <CalendarX className="mr-2 h-4 w-4" />
                        Added to Calendar
                      </>
                    ) : (
                      <>
                        <CalendarPlus className="mr-2 h-4 w-4" />
                        Add to Calendar
                      </>
                    )}
                  </Button> */}
                </div>

                {daysRemaining !== null && daysRemaining <= 2 && (
                  <div className="mt-4 bg-orange-900 p-3 rounded text-orange-200 text-sm flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>
                      Posting deadline in {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-gray-500" />
                    <div>
                      <h3 className="font-medium text-gray-100">Push Notifications</h3>
                      <p className="text-sm text-gray-500">Receive reminders about posting</p>
                    </div>
                  </div>
                  <Switch checked={remindersEnabled} onCheckedChange={handleToggleReminders} />
                </div>

                <div className="border-t border-gray-800 pt-6">
                  <h3 className="font-medium text-gray-100 mb-2">Reminder Schedule</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between text-gray-400">
                      <span>24 hours before deadline</span>
                      <span className={remindersEnabled ? 'text-green-400' : 'text-gray-600'}>
                        {remindersEnabled ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-gray-400">
                      <span>2 hours before deadline</span>
                      <span className={remindersEnabled ? 'text-green-400' : 'text-gray-600'}>
                        {remindersEnabled ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-gray-400">
                      <span>At posting deadline</span>
                      <span className={remindersEnabled ? 'text-green-400' : 'text-gray-600'}>
                        {remindersEnabled ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-foreground shadow-sm rounded-xl overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg text-black font-semibold mb-4">Need to Reschedule?</h2>

              <p className="text-gray-400 mb-4">
                If you need to adjust your posting time, please contact your campaign manager at
                least 24 hours before your scheduled posting time.
              </p>

              <div className="flex flex-col xs:flex-row gap-4">
                <Button
                  variant="outline"
                  className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-gray-100"
                >
                  Contact Manager
                </Button>
                <Button
                  className="flex-1 bg-blue-500 hover:bg-blue-400 text-foreground"
                  onClick={() => {
                    toast('Going to final video', {
                      description: 'Taking you to see your finalized content.',
                    })
                    navigate(`/collaborations/${campaign.id}/final`)
                  }}
                >
                  View Final Video
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="bg-foreground shadow-sm rounded-xl overflow-hidden sticky top-6">
            <div className="p-6">
              <h2 className="text-lg text-black font-semibold mb-4">Campaign Timeline</h2>

              <div className="space-y-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 text-foreground flex items-center justify-center shrink-0 mt-0.5">
                    <Calendar className="h-3 w-3" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-100">Campaign Start</h3>
                    <p className="text-sm text-gray-500">
                      {formatDateOnly(campaign.startDate, 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }) ?? 'TBD'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500 text-foreground flex items-center justify-center shrink-0 mt-0.5">
                    <Calendar className="h-3 w-3" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-100">Content Submitted</h3>
                    <p className="text-sm text-gray-500">
                      {campaign.contentCreatedAt
                        ? format(new Date(campaign.contentCreatedAt), 'MMMM d, yyyy')
                        : 'Pending'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div
                    className={`w-6 h-6 rounded-full ${campaign.contentApprovedAt ? 'bg-green-500 text-foreground' : 'bg-blue-900 text-blue-400'} flex items-center justify-center shrink-0 mt-0.5`}
                  >
                    <Calendar className="h-3 w-3" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-100">Content Approved</h3>
                    <p className="text-sm text-gray-500">
                      {campaign.contentApprovedAt
                        ? format(new Date(campaign.contentApprovedAt), 'MMMM d, yyyy')
                        : 'Pending'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-900 text-orange-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Calendar className="h-3 w-3" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-100">Scheduled Post Date</h3>
                    <p className="text-sm text-gray-500">{formattedDate}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-900 text-gray-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Calendar className="h-3 w-3" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-100">Campaign End</h3>
                    <p className="text-sm text-gray-500">
                      {formatDateOnly(campaign.endDate, 'en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }) ?? 'TBD'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScheduledPost
